import React, { useEffect, useRef } from "react";
import { useI18n } from "../../lib/i18n";

/**
 * Process timeline with sequential reveal-on-scroll.
 * Each step fades up and the orange dot pulses sequentially using
 * IntersectionObserver — pure CSS keyframes, no JS animation cost.
 */
export const ProcessTimeline = () => {
  const { t } = useI18n();
  const stepsRef = useRef([]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const delay = parseInt(e.target.dataset.idx, 10) * 120;
            setTimeout(() => e.target.classList.add("in-view"), delay);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.25 }
    );
    stepsRef.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <section data-testid="process-section" className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{t.process.kicker}</span>
          </div>
          <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-[56px] tracking-tight text-white leading-[1.05]">
            {t.process.title}
          </h2>
        </div>

        <div className="lg:col-span-8 relative">
          <div className="absolute left-[18px] top-2 bottom-2 w-px bg-gradient-to-b from-brand-azure via-brand-orange to-transparent opacity-50" />
          <div className="space-y-10">
            {t.process.steps.map((step, i) => (
              <div
                key={i}
                data-testid={`process-step-${i}`}
                data-idx={i}
                ref={(el) => (stepsRef.current[i] = el)}
                className="relative pl-14 process-step-reveal"
              >
                <div className="process-dot absolute left-0 top-1 w-9 h-9 rounded-full glass-strong flex items-center justify-center font-mono text-xs text-white border border-brand-orange/40">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight">{step.t}</h3>
                <p className="mt-2 text-white/65 leading-relaxed max-w-xl text-[15.5px]">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
